import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { proveedoresAPI } from '../services/proveedoresAPI';
import type { CreateProveedorRequest } from '../services/proveedoresAPI';

// Query keys para React Query
export const proveedoresKeys = {
  all: ['proveedores'] as const,
  lists: () => [...proveedoresKeys.all, 'list'] as const,
  list: (params?: any) => [...proveedoresKeys.lists(), params] as const,
  details: () => [...proveedoresKeys.all, 'detail'] as const,
  detail: (id: number) => [...proveedoresKeys.details(), id] as const,
  categorias: ['proveedores', 'categorias'] as const,
};

// Hook para listar proveedores
export const useProveedores = (params?: {
  search?: string;
  categoria?: string;
  activo?: boolean;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: proveedoresKeys.list(params),
    queryFn: () => proveedoresAPI.listar(params),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};

// Hook para obtener un proveedor específico
export const useProveedor = (id: number) => {
  return useQuery({
    queryKey: proveedoresKeys.detail(id),
    queryFn: () => proveedoresAPI.obtener(id),
    enabled: !!id,
  });
};

// Hook para crear proveedor
export const useCrearProveedor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateProveedorRequest) => proveedoresAPI.crear(data),
    onSuccess: () => {
      // Invalidar todas las queries de proveedores
      queryClient.invalidateQueries({ queryKey: proveedoresKeys.all });
    },
  });
};

// Hook para actualizar proveedor
export const useActualizarProveedor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateProveedorRequest>;
    }) => proveedoresAPI.actualizar(id, data),
    onSuccess: (_, { id }) => {
      // Invalidar queries específicas
      queryClient.invalidateQueries({ queryKey: proveedoresKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: proveedoresKeys.lists() });
    },
  });
};

// Hook para eliminar proveedor
export const useEliminarProveedor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => proveedoresAPI.eliminar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: proveedoresKeys.all });
    },
  });
};

// Hook para reactivar proveedor
export const useReactivarProveedor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => proveedoresAPI.reactivar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: proveedoresKeys.all });
    },
  });
};

// Hook para obtener categorías de proveedores
export const useCategoriasProveedores = () => {
  return useQuery({
    queryKey: proveedoresKeys.categorias,
    queryFn: () => proveedoresAPI.obtenerCategorias(),
    staleTime: 10 * 60 * 1000, // 10 minutos (las categorías no cambian frecuentemente)
  });
};
